<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Pós-vendas</h5>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <!-- “Quando?” dispara a busca ao mudar -->
      <select
        v-model="filters.quando"
        class="form-select same-height-select"
        style="width: 10%;"
        @change="aplicarFiltros"
      >
        <option value="Hoje">Hoje</option>
        <option value="Pendentes">Pendentes</option>
        <option value="Futuros">Futuros</option>
        <option value="Todos">Todos</option>
      </select>

      <!-- “Finalizado” dispara a busca ao mudar -->
      <select
        v-model="filters.finalizado"
        class="form-select same-height-select"
        style="width: 15%;"
        @change="aplicarFiltros"
      >
        <option :value="false">Agendadas</option>
        <option :value="true">Finalizadas</option>
      </select>

      <!-- Status -->
      <select
        v-model="filters.status"
        class="form-select same-height-select"
        style="width: 15%;"
        @change="aplicarFiltros"
      >
        <option value="Todos">Status: Todos</option>
        <option value="Ativo">Ativo</option>
        <option value="Trancado">Trancado</option>
        <option value="Encerrado">Encerrado</option>
        <option value="Arquivado">Arquivado</option>
      </select>

      <!-- “Aluno” dispara a busca em cada digitação -->
      <input
        v-model="filters.aluno"
        placeholder="Aluno"
        class="form-control same-height"
        style="width: 44%;"
        @input="aplicarFiltros"
      />

      <!-- Botão Limpar mantém a ação antiga -->
      <b-button variant="secondary" @click="limparFiltros" class="d-flex align-items-center justify-content-center">
        <i class="bi bi-funnel mr-2" title="Limpar Filtro"></i>
      </b-button>
    </div>

    <!-- Tabela customizada -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="sort('matricula_id')">
              Matrícula {{ arrow('matricula_id') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('aluno')">
              Aluno {{ arrow('aluno') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('finalizado')">
              Finalizado {{ arrow('finalizado') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('proximo_contato')">
              Agendamento {{ arrow('proximo_contato') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="sort('status')">
              Status {{ arrow('status') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in posvendas" :key="item.id">
          <td>{{ item.matricula_id }}</td>
          <td>{{ item.aluno_nome }}</td>
          <td>{{ item.finalizado ? 'Sim' : 'Não' }}</td>
          <td>{{ utils.formatarData(item.proximo_contato) }}</td>
          <td><span :class="statusClass(item.status_exibido)">{{ item.status_exibido }}</span></td>
          <td class="text-end">
            <i
              v-if="hasPermission('realizar_posvendas')"
              class="bi bi-headset text-warning mx-2"
              title="Realizar Pós-venda"
              style="cursor: pointer;"
              @click="abrirModalRealizar(item)"
            ></i>
            <router-link
              v-if="hasPermission('detalhar_posvendas')"
              :to="`/operacional/posvendas/${item.matricula_id}/detalhar`"
            >
              <i class="bi bi-eye-fill text-primary mx-2" title="Detalhar"></i>
            </router-link>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <!-- 1. Resumo de registros -->
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalRegistros }} registros
      </div>

      <!-- 2. Seletor de itens por página -->
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limitePorPagina"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
        />
      </div>

      <!-- 3. Controle de página -->
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="paginaAtual"
          :per-page="limitePorPagina"
          :total-rows="totalRegistros"
          size="sm"
          limit="5"
        />
      </div>
    </div>

    <!-- Modal RealizarPós-venda -->
    <RealizarPosvenda
      :mostrar="modalRealizar"
      :posvenda="posvendaSelecionada"
      :aluno="posvendaSelecionada?.aluno_nome || ''"
      @fechar="modalRealizar = false"
      @salvou="atualizarPosvendas"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import axios from "@/services/axios";
import { BButton, BFormSelect, BPagination } from "bootstrap-vue-next";
import RealizarPosvenda from "@/components/modals/RealizarPosvenda.vue";
import { usePermissions } from "@/composables/usePermissions";
import globalUtils from "@/utils/globalUtils.js";

const posvendas           = ref([]);
const paginaAtual         = ref(1);
const limitePorPagina     = ref(10);
const totalPaginas        = ref(1);
const totalRegistros      = ref(0);
const sortField           = ref("proximo_contato");
const sortOrder           = ref("asc");
const filters             = reactive({ quando: "Hoje", finalizado: false, aluno: "", status: "Todos" });
const modalRealizar       = ref(false);
const posvendaSelecionada = ref(null);

const pageOptions = [10, 15, 20];

const { fetchPermissions, hasPermission } = usePermissions();

// exposer globalUtils to template if needed
const utils = globalUtils;

// Computeds para resumo
const startRow = computed(() =>
  (paginaAtual.value - 1) * limitePorPagina.value + 1
);
const endRow = computed(() =>
  Math.min(paginaAtual.value * limitePorPagina.value, totalRegistros.value)
);

// Watchers para recarregar ao mudar página ou limite
watch(paginaAtual, () => carregarPosvendas());
watch(limitePorPagina, () => {
  paginaAtual.value = 1;
  carregarPosvendas();
});

async function carregarPosvendas() {
  try {
    const res = await axios.get("/operacional/posvendas", {
      params: {
        page:       paginaAtual.value,
        limit:      limitePorPagina.value,
        sortField:  sortField.value,
        sortOrder:  sortOrder.value,
        aluno:      filters.aluno,
        finalizado: filters.finalizado,
        quando:     filters.quando,
        status:     filters.status
      }
    });
    // agora sim destructuring correto:
    const { data, totalPages, total } = res.data;
    posvendas.value      = data || [];
    totalPaginas.value   = totalPages ?? 1;
    totalRegistros.value = total     ?? data.length;
  } catch (e) {
    console.error("Erro ao carregar pós-vendas:", e);
  }
}

function sort(col) {
  if (sortField.value === col) {
    sortOrder.value = sortOrder.value === "asc" ? "desc" : "asc";
  } else {
    sortField.value = col;
    sortOrder.value = "asc";
  }
  paginaAtual.value = 1;
  carregarPosvendas();
}

function arrow(col) {
  if (sortField.value !== col) return "";
  return sortOrder.value === "asc" ? "▲" : "▼";
}

function aplicarFiltros() {
  paginaAtual.value = 1;
  carregarPosvendas();
}

function limparFiltros() {
  filters.quando     = "Hoje";
  filters.finalizado = false;
  filters.aluno      = "";
  filters.status     = "Todos";
  paginaAtual.value  = 1;
  carregarPosvendas();
}

// helper visual idêntico ao ListarMatriculas.vue
function statusClass(status) {
  if (status === 'Ativo')     return 'text-success';
  if (status === 'Trancado')  return 'text-danger';
  if (status === 'Encerrado') return 'text-brown';
  if (status === 'Arquivado') return 'text-primary';
  return '';
}

function abrirModalRealizar(item) {
  posvendaSelecionada.value = item;
  modalRealizar.value        = true;
}

function atualizarPosvendas() {
  modalRealizar.value = false;
  carregarPosvendas();
}

onMounted(async () => {
  await fetchPermissions();
  carregarPosvendas();
});
</script>






<style scoped>
.same-height { height: 30px; padding: 6px 12px; font-size: .72rem; }
.same-height-select {
  height: 30px;
  padding: 6px 2.25rem 6px 0.75rem;
  border-radius: 0 !important;
}

:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}

</style>